"use client"

import { useState, useEffect } from "react"
import SettingsRailLayout from "../../components/settings-rail-layout"
import { leadbridgeAPI, territoriesAPI, locationsAPI } from "../../services/api"
import LeadBridgeSemanticDiagnostics from "../../components/LeadBridgeSemanticDiagnostics"
import {
  Star, ThumbsUp, Zap, Loader2, RefreshCw, Eye, EyeOff,
} from "lucide-react"

function StatusBadge({ status }) {
  const styles = { connected: 'bg-green-100 text-green-700', not_connected: 'bg-gray-100 text-gray-600' }
  const labels = { connected: 'Connected', not_connected: 'Not connected' }
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[status]}`}>{labels[status]}</span>
}

const LeadBridgeSettings = () => {
  const [loading, setLoading] = useState(true)

  const [lbConnected, setLbConnected] = useState(false)
  const [lbAccounts, setLbAccounts] = useState([])
  const [lbConnectedAt, setLbConnectedAt] = useState(null)
  const [showLbConnectModal, setShowLbConnectModal] = useState(false)
  const [lbEmail, setLbEmail] = useState('')
  const [lbPassword, setLbPassword] = useState('')
  const [lbPasswordVisible, setLbPasswordVisible] = useState(false)
  const [lbConnecting, setLbConnecting] = useState(false)
  const [lbConnectError, setLbConnectError] = useState('')
  const [lbSyncing, setLbSyncing] = useState(false)
  const [lbSyncProgress, setLbSyncProgress] = useState(null)

  const [territories, setTerritories] = useState([])
  const [locationMappings, setLocationMappings] = useState([])

  useEffect(() => { loadLbStatus() }, [])

  const loadLbStatus = async () => {
    setLoading(true)
    try {
      const status = await leadbridgeAPI.getStatus()
      setLbConnected(status.connected)
      setLbAccounts(status.accounts || [])
      setLbConnectedAt(status.connectedAt)
    } catch (e) { /* not connected */ }
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const terrData = await territoriesAPI.getAll(user.id, { status: 'active', limit: 100 })
      setTerritories((terrData?.territories || terrData || []).map(t => ({ id: t.id, name: t.name })))
    } catch (e) { /* no territories */ }
    try {
      const mappingData = await locationsAPI.getMappings()
      setLocationMappings(mappingData.mappings || [])
    } catch (e) { /* no mappings */ }
    setLoading(false)
  }

  const handleLocationMapping = async (accountId, territoryId, channel) => {
    try {
      await locationsAPI.createMapping({
        providerAccountId: accountId,
        sfLocationId: parseInt(territoryId),
        provider: 'leadbridge',
        channel: channel || 'thumbtack',
        mappingType: 'account_level',
      })
      const mappingData = await locationsAPI.getMappings()
      setLocationMappings(mappingData.mappings || [])
    } catch (e) {
      alert('Failed to save location mapping: ' + (e.response?.data?.error || e.message))
    }
  }

  const handleLbConnect = async () => {
    if (!lbEmail.trim() || !lbPassword.trim()) { setLbConnectError('Email and password required'); return }
    setLbConnecting(true); setLbConnectError('')
    try {
      await leadbridgeAPI.connect(lbEmail.trim(), lbPassword.trim())
      setLbConnected(true)
      setLbConnectedAt(new Date().toISOString())
      await loadLbStatus()
      setShowLbConnectModal(false)
      setLbEmail(''); setLbPassword('')
    } catch (e) {
      setLbConnectError(e.response?.data?.error || 'Failed to connect. Check your credentials.')
    } finally { setLbConnecting(false) }
  }

  const handleLbDisconnect = async () => {
    if (!window.confirm('Disconnect LeadBridge? Thumbtack and Yelp messages will stop syncing.')) return
    try {
      await leadbridgeAPI.disconnect()
      setLbConnected(false); setLbAccounts([]); setLbConnectedAt(null)
    } catch (e) { alert('Failed to disconnect') }
  }

  const handleLbSync = async (limit) => {
    setLbSyncing(true); setLbSyncProgress({ status: 'running', total: 0, synced: 0, messages: 0 })
    try {
      leadbridgeAPI.sync(null, limit || undefined).catch(() => {})
      const pollInterval = setInterval(async () => {
        try {
          const progress = await leadbridgeAPI.getSyncProgress()
          setLbSyncProgress(progress)
          if (progress.status === 'complete' || progress.status === 'error') {
            clearInterval(pollInterval)
            setLbSyncing(false)
          }
        } catch (e) { /* keep polling */ }
      }, 3000)
      setTimeout(() => { clearInterval(pollInterval); setLbSyncing(false) }, 300000)
    } catch (e) { alert('Sync failed'); setLbSyncing(false) }
  }

  const status = lbConnected ? 'connected' : 'not_connected'

  return (
    <SettingsRailLayout
      title="LeadBridge"
      section="Integrations"
      subtitle="Connect your Thumbtack and Yelp accounts to receive leads and messages"
    >
      {loading ? (
        <div className="flex items-center justify-center p-20"><Loader2 size={32} className="animate-spin text-[var(--sf-text-muted)]" /></div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-[var(--sf-border-light)] overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-2.5 rounded-lg ${status === 'connected' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                  <Zap size={22} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--sf-text-primary)]">Thumbtack / Yelp via LeadBridge</span>
                    <StatusBadge status={status} />
                  </div>
                  <p className="text-xs text-[var(--sf-text-muted)] mt-0.5">Connect your Thumbtack and Yelp accounts to receive leads and messages</p>
                  {status === 'connected' && lbAccounts.length > 0 && (
                    <div className="mt-2 space-y-2 w-full">
                      {lbAccounts.map((a, i) => {
                        const mapping = locationMappings.find(m => m.providerAccountId === a.id)
                        const mappedTerritoryId = mapping?.sfLocationId || ''
                        return (
                          <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                            <span className={`p-1 rounded ${a.channel === 'yelp' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                              {a.channel === 'yelp' ? <Star size={12} /> : <ThumbsUp size={12} />}
                            </span>
                            <span className="text-xs font-medium text-[var(--sf-text-primary)] flex-1 truncate">
                              {a.displayName || a.channel}
                            </span>
                            {territories.length > 0 ? (
                              <select
                                value={mappedTerritoryId}
                                onChange={e => {
                                  if (e.target.value) handleLocationMapping(a.id, e.target.value, a.channel)
                                }}
                                className="text-[11px] border border-[var(--sf-border-light)] rounded px-2 py-1 bg-white min-w-[120px]">
                                <option value="">{mappedTerritoryId ? 'Change location...' : 'Assign location'}</option>
                                {territories.map(t => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-[10px] text-[var(--sf-text-muted)]">No locations</span>
                            )}
                            {mapping && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                                {mapping.locationName}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {status === 'connected' && (
                  <>
                    <button onClick={() => handleLbSync(10)} disabled={lbSyncing}
                      className="px-3 py-1.5 text-xs font-medium bg-[var(--sf-blue-500)] text-white rounded-lg hover:bg-[var(--sf-blue-600)] disabled:opacity-50 flex items-center gap-1">
                      {lbSyncing ? <><Loader2 size={12} className="animate-spin" /> Syncing...</> : <><RefreshCw size={12} /> Test (10)</>}
                    </button>
                    <button onClick={() => handleLbSync()} disabled={lbSyncing}
                      className="px-3 py-1.5 text-xs font-medium border border-[var(--sf-border-light)] rounded-lg hover:bg-[var(--sf-bg-hover)] text-[var(--sf-text-secondary)] disabled:opacity-50 flex items-center gap-1">
                      <RefreshCw size={12} /> Sync All
                    </button>
                    <button onClick={handleLbDisconnect}
                      className="px-3 py-1.5 text-xs font-medium border border-red-200 rounded-lg hover:bg-red-50 text-red-600">
                      Disconnect
                    </button>
                  </>
                )}
                {status === 'not_connected' && (
                  <button onClick={() => { setShowLbConnectModal(true); setLbConnectError('') }}
                    className="px-3 py-1.5 text-xs font-medium bg-[var(--sf-blue-500)] text-white rounded-lg hover:bg-[var(--sf-blue-600)]">
                    Connect
                  </button>
                )}
              </div>
            </div>
            {lbSyncing && (
              <div className="px-4 pb-4">
                <div className="bg-[var(--sf-bg-input)] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-[var(--sf-text-primary)]">
                      {lbSyncProgress?.phase?.startsWith('syncing_') ? `Syncing ${lbSyncProgress.phase.replace('syncing_', '')}...` : 'Syncing...'}
                    </span>
                    <span className="text-xs text-[var(--sf-text-muted)]">
                      {lbSyncProgress ? `${lbSyncProgress.synced}/${lbSyncProgress.total} leads, ${lbSyncProgress.messages} msgs` : 'Starting...'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div className="bg-[var(--sf-blue-500)] h-1.5 rounded-full transition-all"
                      style={{ width: `${lbSyncProgress?.total > 0 ? Math.round((lbSyncProgress.synced / lbSyncProgress.total) * 100) : 5}%` }} />
                  </div>
                  {lbSyncProgress?.errors > 0 && <p className="text-[10px] text-red-500 mt-1">{lbSyncProgress.errors} errors</p>}
                </div>
              </div>
            )}
            {lbSyncProgress?.status === 'complete' && !lbSyncing && (
              <div className="px-4 pb-4">
                <div className="bg-green-50 rounded-lg p-3 text-xs text-green-700">
                  Synced {lbSyncProgress.synced} conversations, {lbSyncProgress.messages} messages
                </div>
              </div>
            )}
            {lbConnected && (
              <div className="px-4 pb-4">
                <LeadBridgeSemanticDiagnostics />
              </div>
            )}
          </div>
        </div>
      )}

      {showLbConnectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-[var(--sf-text-primary)] mb-2">Connect Thumbtack / Yelp</h3>
            <p className="text-sm text-[var(--sf-text-muted)] mb-4">Sign in with your LeadBridge account to import Thumbtack and Yelp conversations.</p>
            {lbConnectError && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">{lbConnectError}</div>}
            <input type="email" value={lbEmail} onChange={e => setLbEmail(e.target.value)} placeholder="LeadBridge email"
              className="w-full border border-[var(--sf-border-light)] rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-1 focus:ring-[var(--sf-blue-500)]" />
            <div className="relative mb-4">
              <input type={lbPasswordVisible ? "text" : "password"} value={lbPassword} onChange={e => setLbPassword(e.target.value)} placeholder="Password"
                onKeyDown={e => e.key === 'Enter' && handleLbConnect()}
                className="w-full border border-[var(--sf-border-light)] rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--sf-blue-500)]" />
              <button
                type="button"
                onClick={() => setLbPasswordVisible(v => !v)}
                aria-label={lbPasswordVisible ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--sf-text-muted)] hover:text-[var(--sf-text-primary)] focus:outline-none"
              >
                {lbPasswordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowLbConnectModal(false)} className="px-4 py-2 text-sm text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)]">Cancel</button>
              <button onClick={handleLbConnect} disabled={lbConnecting}
                className="px-4 py-2 text-sm bg-[var(--sf-blue-500)] text-white rounded-lg hover:bg-[var(--sf-blue-600)] disabled:opacity-50 flex items-center gap-2">
                {lbConnecting && <Loader2 size={14} className="animate-spin" />} {lbConnecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SettingsRailLayout>
  )
}

export default LeadBridgeSettings
